import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, output } from "@angular/core";
import { IDocumentModel, IInvoiceModel } from "@models";
import { DocumentsService, DocumentTypes, FunctionLockService, GlobalsService, HttpService, InvoicesService } from "@services";
import * as moment from 'moment';
import Swal from "sweetalert2";
import { SlickScreenBlockerService } from "../../../../libraries/slick-components";

interface PhotoGroup {
	dateKey: string;
	label: string;
	photos: IDocumentModel[];
}

@Component({
	selector: "documents-edit",
	templateUrl: "./documents-edit.component.html",
	styleUrls: ["./documents-edit.component.css"],
	providers: [DocumentsService,InvoicesService]
})
export class DocumentsEditComponent implements OnChanges {
	@Input() invoiceModel: IInvoiceModel;
	@Input("title") title: string;
	@Input("documentKey") documentKey: string;
	@Input("referenceId") referenceId: number;
	@Input("excludeDocumentKey") excludeDocumentKey: string;
	@Input() primaryJobDocumentKey: string;
	// Sent with getUrl so an invoice/estimate/sales order gets its job's (jobsite) customer's photos, not the bill-to's
	@Input() jobId: number;
	@Input("documentType") documentType: DocumentTypes;
	@Input('canDelete') canDelete: boolean;
	@Input('folderType') folderType: string;
	@Input('hideFileDrop') hideFileDrop: boolean;
	@Input('getUrl') getUrl: string;
	@Input("allowTitleEditing") allowTitleEditing: boolean = true;
    @Input() showCheckboxes: boolean = false;
	@Input() hideList: boolean = false;
    @Input() fileFilter: 'all' | 'photos' | 'documents' = 'all';

    @Output() onAdd : EventEmitter<void> = new EventEmitter();

	imageExts: string[] = ['.jpg', '.jpeg', '.bmp', '.png', '.gif'];
    videoExts: string[] = ['.mp4', '.avi', '.mov', '.wmv', '.flv'];

	photos: IDocumentModel[];
	documents: IDocumentModel[];
	photoGroups: PhotoGroup[] = [];

	constructor(private readonly documentsService: DocumentsService,
		private readonly httpService: HttpService,
		private readonly functionLockService: FunctionLockService,
        private readonly slickScreenBlockerService: SlickScreenBlockerService,
	private readonly invoicesService: InvoicesService) {
	}

	async ngOnChanges(changes: SimpleChanges) {
		/*GlobalsService.checkPermission(feature, action)*/
		if (changes.documentType) {
			switch (this.documentType.toString().toLowerCase()) {
				case "customers":
					this.documentType = DocumentTypes.Customers;
					break;

				case "jobs":
					this.documentType = DocumentTypes.Jobs;
					break;

				case "invoices":
					this.documentType = DocumentTypes.Invoices;
					break;

				case "payments":
					this.documentType = DocumentTypes.Payments;
					break;

				case "lathamorders":
					this.documentType = DocumentTypes.LathamOrders;
					break;

				case "users":
					this.documentType = DocumentTypes.Users;
					break;
			}
		}

		if ((changes.documentKey || changes.jobId) && this.documentKey)
			this.reload();
	}

	async onFileDeleted(file: IDocumentModel) {
		await this.documentsService.deleteDocument(file.documentId);
        this.photos = (this.photos || []).filter(photo => photo.documentId !== file.documentId);
        this.documents = (this.documents || []).filter(document => document.documentId !== file.documentId);
        this.buildPhotoGroups();
	}

	async onFileChanged(fileModel: IDocumentModel) {
		await this.documentsService.updateDocument(fileModel);
    }

    async onNewFilesAdded(files: IDocumentModel[]) {
        if (this.folderType.toLowerCase() === 'photos') {
            let validExt = true;
            let showBlocker = false;
            files.forEach(x => {
                if (!this.imageExts.includes(".".concat(x.file.type.split("/")[1])) && !this.videoExts.includes(".".concat(x.file.type.split("/")[1]))) {
                    validExt = false;
                }

                if (this.videoExts.includes(".".concat(x.file.type.split("/")[1]))) {
                    showBlocker = true;
                }
            })
            try {

                if (showBlocker && validExt) {
					this.slickScreenBlockerService.block();
				}
				if (validExt) {
					await this.documentsService.updatePhotos(this.documentKey, this.documentType, files);
				}
				else {
					await Swal.fire({
						icon: 'warning',
						title: 'Oops',
						text: 'Unsupported file type',
						confirmButtonColor: '#007bff',
						width: '28em',
						heightAuto: false
					});
					return;
				}
			}
			finally {
                this.slickScreenBlockerService.unblock();
			}
		}
		else if (this.folderType.toLowerCase() === 'documents')
			await this.documentsService.updateDocuments(this.documentKey, this.documentType, files);

		this.reload();
        this.onAdd.emit();
	}

	async onDocumentClicked(fileModel: IDocumentModel) {
		if (fileModel.mimeType === 'application/pdf') {
			const w = window.open('', '_blank');
			w.location.href = fileModel.url;
		}
		else
			this.documentsService.openDocument(fileModel.documentId);
	}

	public async reload() {
		if (this.getUrl) {
			let params: any = { documentKey: this.documentKey };
			if (this.referenceId)
				params = { referenceId: this.referenceId };
			else if (this.jobId)
				params.jobId = this.jobId;

			const allDocuments = <IDocumentModel[]>(await this.httpService.get(this.getUrl, params));
			this.documents = this.getDocuments(allDocuments);
			this.photos = this.getPhotos(allDocuments);
			this.buildPhotoGroups();
		}
		else {
			if (this.folderType && this.folderType.toLowerCase() === 'photos') {
				this.documents = [];
				this.photos = await this.documentsService.getPhotos(this.documentKey, this.documentType);
				this.buildPhotoGroups();
			}
			else if (this.folderType && this.folderType.toLowerCase() === 'documents') {
				let allDocuments = await this.documentsService.getDocuments(this.documentKey, this.documentType);
				this.documents = this.getDocuments(allDocuments);
				this.photos = this.getPhotos(allDocuments);
				this.buildPhotoGroups();
			}
		}

        // Customer photo and attachment views share an API result; separate by file type.
        if (this.fileFilter !== 'all') {
            const files = [...(this.photos || []), ...(this.documents || [])];
            const isPhoto = (file: IDocumentModel) => {
                const mimeType = (file.mimeType || '').toLowerCase();
                const path = (file.url || '').split(/[?#]/)[0].toLowerCase();
                return mimeType.startsWith('image/') || this.imageExts.some(ext => path.endsWith(ext));
            };
            this.photos = this.fileFilter === 'photos' ? files.filter(isPhoto) : [];
            this.documents = this.fileFilter === 'documents' ? files.filter(file => !isPhoto(file)) : [];
            this.buildPhotoGroups();
        }

		if (this.invoiceModel) {
			if (this.photos?.length > 0 && this.invoiceModel.selectedJobPhotoDocumentIds?.length > 0) {
				this.photos.forEach(p => {
					const isChecked = this.invoiceModel.selectedJobPhotoDocumentIds.find(x => x === p.documentId);
					if (isChecked)
						p.isChecked = true;
				});
			}
		}

		if (this.excludeDocumentKey) {
			this.photos = this.photos.filter(x => x.documentKey !== this.excludeDocumentKey);
			this.documents = this.documents.filter(x => x.documentKey !== this.excludeDocumentKey);
		}
	}

	private buildPhotoGroups() {
		this.photoGroups = [];

		if (!this.photos || this.photos.length === 0)
			return;

		const byDate = new Map<string, IDocumentModel[]>();

		this.photos.forEach(p => {
			const dt = p.fileDate ? moment(p.fileDate) : null;
			const key = dt ? dt.format('YYYY-MM-DD') : 'Unknown';
			if (!byDate.has(key)) byDate.set(key, []);
			byDate.get(key).push(p);
		});
		const keys = Array.from(byDate.keys()).sort((a, b) => {
			const aHasPrimary = this.primaryJobDocumentKey
				? (byDate.get(a) || []).some(p => p.documentKey === this.primaryJobDocumentKey)
				: false;
			const bHasPrimary = this.primaryJobDocumentKey
				? (byDate.get(b) || []).some(p => p.documentKey === this.primaryJobDocumentKey)
				: false;

			if (aHasPrimary && !bHasPrimary) return -1;
			if (!aHasPrimary && bHasPrimary) return 1;

			// Newest dates first
			return b.localeCompare(a);
		});

		this.photoGroups = keys.map(k => {
			const dt = k === 'Unknown' ? null : moment(k, 'YYYY-MM-DD');
			const label = dt ? dt.format('MMM D, YYYY') : 'Unknown Date';
			const photos = (byDate.get(k) || []).slice().sort((a, b) =>
				new Date(b.fileDate ?? 0).getTime() - new Date(a.fileDate ?? 0).getTime()
			);

			return { dateKey: k, label, photos };
		});
	}

	private getDocuments(documents: IDocumentModel[]): IDocumentModel[] {
		const allDocuments = documents.map(doc => {
			doc.isImage = false;

			this.imageExts.forEach(ext => {
				if (doc.url.indexOf(ext) >= 0)
					doc.isImage = true;
			});

			return doc;
		})

		return allDocuments.filter(x => x.isImage === false);
	}

	private getPhotos(documents: IDocumentModel[]): IDocumentModel[] {
		const allDocuments = documents.map(doc => {
			doc.isImage = false;

			this.imageExts.forEach(ext => {
				if (doc.url.indexOf(ext) >= 0)
					doc.isImage = true;
			});

			return doc;
		})

		return allDocuments.filter(x => x.isImage === true);
	}

	async onFileCheckChanged(fileModel: any) {
		if (!this.invoiceModel)
			return;
		await this.functionLockService.lock("ON_FILE_CHECK_CHANGED");
		try {
			await this.invoicesService.updateJobPhotoCheckChanged(this.invoiceModel.invoiceId, fileModel.documentId, fileModel.isChecked);
		}
		finally {
			this.functionLockService.release("ON_FILE_CHECK_CHANGED");
		}
	}

}

